import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GuideTourContext from './GuideTour.context';
import {
  getArrayLength,
  isArrowLeftPressed,
  isArrowRightPressed,
  isEscapePressed,
  scrollToElement,
} from 'utils';
import useMatchMedia from 'hooks/useMatchMedia';
import { useNavigate, useLocation } from 'react-router-dom';
import { KeyEvent } from 'types';
import GuideCard from 'components/GuideCard';

type GuideStep = {
  selector: string;
  mobileSelector?: string;
  title: string;
  description: string;
  path?: string;
};

type GuideTourProps = { guideSteps: GuideStep[] };

const GuideTour: React.FC<GuideTourProps> = (props) => {
  const { children, guideSteps } = props;

  const [showGuide, setShowGuide] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const navigate = useNavigate();

  const { pathname } = useLocation();
  const isPhablet = useMatchMedia('isPhablet');

  const totalNumbeOfSteps = getArrayLength(guideSteps);
  const canUserSeeGuide = !!totalNumbeOfSteps;

  const selectedGuideStep = useMemo(
    () => guideSteps[currentStep],
    [currentStep, guideSteps],
  );

  const { selector, mobileSelector, title, description } =
    selectedGuideStep || {};

  const elementSelector = useMemo(
    () => (isPhablet && mobileSelector ? `#${mobileSelector}` : `#${selector}`),
    [isPhablet, mobileSelector, selector],
  );

  const handleNext = useCallback(() => {
    setCurrentStep((prev) => {
      const newStep = (prev % totalNumbeOfSteps) + 1;

      const { path } = guideSteps[newStep];

      if (path && !pathname.includes(path)) navigate(path);

      return newStep;
    });
  }, [totalNumbeOfSteps, guideSteps, pathname, navigate]);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => {
      const newStep = prev - 1;

      if (newStep < 0) return 0;

      const { path } = guideSteps[newStep];

      if (path && !pathname.includes(path)) navigate(path);

      return newStep;
    });
  }, [guideSteps, navigate, pathname]);

  const onShowGuide = useCallback(() => {
    const newStepPath = guideSteps[0].path;

    if (newStepPath && !pathname.includes(newStepPath)) navigate(newStepPath);

    setCurrentStep(0);
    setShowGuide(true);
  }, [guideSteps, pathname, navigate]);

  const onCloseOnboarding = useCallback(() => {
    setCurrentStep(0);
    setShowGuide(false);
  }, []);

  const onKeyDown = useCallback(
    (event: KeyEvent) => {
      if (!canUserSeeGuide || !showGuide) return;

      if (isEscapePressed(event)) {
        onCloseOnboarding();
        return;
      }

      if (isArrowRightPressed(event)) {
        if (currentStep === totalNumbeOfSteps - 1) onCloseOnboarding();
        else handleNext();
        return;
      }

      if (isArrowLeftPressed(event)) {
        handleBack();
      }
    },
    [
      canUserSeeGuide,
      showGuide,
      currentStep,
      totalNumbeOfSteps,
      handleBack,
      handleNext,
      onCloseOnboarding,
    ],
  );

  useEffect(() => {
    if (!showGuide || !selector) return;

    const id = requestAnimationFrame(() => scrollToElement(elementSelector));

    return () => cancelAnimationFrame(id);
  }, [currentStep, showGuide, selector, elementSelector]);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);

  return (
    <GuideTourContext.Provider value={{ onShowGuide }}>
      {children}
      {showGuide && (
        <GuideCard
          preferredPosition="bottom"
          title={title}
          description={description}
          stepNumber={currentStep + 1}
          totalSteps={totalNumbeOfSteps}
          showBannerNotification
          selector={elementSelector}
          onBack={handleBack}
          onNext={handleNext}
          onClose={onCloseOnboarding}
        />
      )}
    </GuideTourContext.Provider>
  );
};

export default GuideTour;
